import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { statsAPI } from '../api/api';
import PlayerAvatar from '../components/PlayerAvatar';
import BadgeCard, { BADGE_TIER_RANK } from '../components/BadgeCard';

// ── Display helpers (mirrors PlayerDetail.js) ──────────────────────────────
const CATEGORY_ORDER = ['outsideScoring', 'insideScoring', 'athleticism', 'playmaking', 'defense', 'rebounding', 'intangibles'];

// 2kratings-style value badge: solid colored square, white number.
const attrBadgeClass = (v) => {
  if (v == null) return 'bg-gray-400 dark:bg-gray-600';
  if (v >= 90) return 'bg-emerald-600';
  if (v >= 80) return 'bg-green-600';
  if (v >= 70) return 'bg-amber-500';
  if (v >= 60) return 'bg-orange-500';
  return 'bg-red-600';
};

const prettyAttr = (key) => {
  const s = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const BADGE_CATEGORY_ORDER = ['Shooting', 'Playmaking', 'Finishing', 'Physical', 'Defense', 'Rebounding'];

// Parse "24.5" / "-" / null → number or null
const toNum = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

// Which side wins a numeric row. Returns 'a', 'b' or null (tie / missing).
const winnerOf = (a, b, higherIsBetter = true) => {
  if (a == null || b == null || a === b) return null;
  const aWins = higherIsBetter ? a > b : a < b;
  return aWins ? 'a' : 'b';
};

// ── Searchable player picker ───────────────────────────────────────────────
const PlayerPicker = ({ players, value, onSelect, placeholder }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q
      ? players.filter(p => `${p.firstName} ${p.lastName}`.toLowerCase().includes(q))
      : players;
    return base.slice(0, 60);
  }, [players, query]);

  return (
    <div className="relative" ref={boxRef}>
      <input
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
      />
      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl">
          {filtered.length === 0 ? (
            <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">{t('compare.noResults')}</div>
          ) : (
            filtered.map(p => (
              <button
                key={p.id}
                onClick={() => { onSelect(p.id); setOpen(false); setQuery(''); }}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition ${
                  value === p.id ? 'bg-blue-50 dark:bg-blue-900/30' : ''
                }`}
              >
                <span className="inline-flex items-center justify-center min-w-[2rem] h-6 px-1 rounded text-xs font-bold text-white bg-blue-600">
                  {p.overallRating ?? '–'}
                </span>
                <span className="flex-1 truncate text-sm text-gray-900 dark:text-gray-100">
                  {p.firstName} {p.lastName}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">{p.position}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded ${
                  p.isFreeAgent
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                    : 'text-gray-400 dark:text-gray-500'
                }`}>
                  {p.isFreeAgent ? t('compare.freeAgent') : (p.team?.abbreviation || p.team?.name || '')}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ── One head-to-head numeric row ───────────────────────────────────────────
const CompareRow = ({ label, a, b, higherIsBetter = true, suffix = '' }) => {
  const win = winnerOf(a, b, higherIsBetter);
  const fmt = (v) => (v == null ? '–' : `${v}${suffix}`);
  const cell = (side, v) =>
    `text-center font-semibold tabular-nums px-2 py-1.5 rounded ${
      win === side
        ? 'text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20'
        : 'text-gray-800 dark:text-gray-200'
    }`;
  return (
    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 py-0.5">
      <div className={cell('a', a)}>{fmt(a)}</div>
      <div className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide min-w-[5.5rem]">{label}</div>
      <div className={cell('b', b)}>{fmt(b)}</div>
    </div>
  );
};

const SectionCard = ({ title, children }) => (
  <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900 rounded-lg p-5 mb-6">
    <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">{title}</h2>
    {children}
  </div>
);

// ── Selected-player header card with change/clear controls ────────────────
const SlotHeader = ({ slotLabel, players, value, onSelect, onClear, data }) => {
  const { t } = useTranslation();
  const player = data?.player;
  return (
    <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900 rounded-lg p-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">{slotLabel}</div>
      {player ? (
        <div className="flex items-center gap-4">
          <PlayerAvatar player={player} size="medium" />
          <div className="flex-1 min-w-0">
            <Link to={`/player/${player.id}`} className="text-lg font-bold text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 truncate block">
              {player.firstName} {player.lastName}
            </Link>
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 flex-wrap">
              {player.overallRating != null && (
                <span className="bg-blue-600 text-white px-2 py-0.5 rounded font-bold text-xs">{player.overallRating} OVR</span>
              )}
              <span>{player.position}</span>
              <span>·</span>
              <span className="truncate">
                {player.team ? `${player.team.city} ${player.team.name}` : t('compare.freeAgent')}
              </span>
            </div>
          </div>
          <button onClick={onClear} className="text-sm text-gray-400 hover:text-red-500 dark:hover:text-red-400 shrink-0" title={t('compare.clear')}>✕</button>
        </div>
      ) : (
        <PlayerPicker players={players} value={value} onSelect={onSelect} placeholder={t('compare.searchPlaceholder')} />
      )}
    </div>
  );
};

const ComparePlayers = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const idA = searchParams.get('a');
  const idB = searchParams.get('b');
  const type = searchParams.get('type') === 'playoffs' ? 'playoffs' : 'regular';

  const [allPlayers, setAllPlayers] = useState([]);
  const [dataA, setDataA] = useState(null);
  const [dataB, setDataB] = useState(null);

  // Load the full player universe (rostered + free agents) once for the pickers
  useEffect(() => {
    api.get('/players/page-data')
      .then(res => {
        const rostered = (res.data.players || []).map(p => ({ ...p, isFreeAgent: false }));
        const rosteredIds = new Set(rostered.map(p => p.id));
        const fas = (res.data.freeAgents || [])
          .filter(p => !rosteredIds.has(p.id))
          .map(p => ({ ...p, isFreeAgent: true }));
        const combined = [...rostered, ...fas].sort((a, b) =>
          `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`)
        );
        setAllPlayers(combined);
      })
      .catch(err => console.error('Failed to load players for comparison:', err));
  }, []);

  // Load one slot's detail + career averages. Stable per `type` so the
  // per-slot effects below have a clean dependency list.
  const loadSlot = useCallback(async (id, setter) => {
    if (!id) { setter(null); return; }
    setter(prev => ({ ...(prev || {}), loading: true }));
    try {
      const [playerRes, careerRes] = await Promise.all([
        api.get(`/players/${id}`),
        statsAPI.getCareerAverages(id, type).catch(() => ({ data: null }))
      ]);
      setter({ player: playerRes.data, career: careerRes.data, loading: false });
    } catch (err) {
      console.error('Failed to load compared player:', err);
      setter({ player: null, career: null, loading: false, error: true });
    }
  }, [type]);

  useEffect(() => { loadSlot(idA, setDataA); }, [idA, loadSlot]);
  useEffect(() => { loadSlot(idB, setDataB); }, [idB, loadSlot]);

  const setParam = (key, val) => {
    const next = new URLSearchParams(searchParams);
    if (val == null || val === '') next.delete(key);
    else next.set(key, val);
    setSearchParams(next, { replace: false });
  };

  const swap = () => {
    const next = new URLSearchParams(searchParams);
    if (idA) next.set('b', idA); else next.delete('b');
    if (idB) next.set('a', idB); else next.delete('a');
    setSearchParams(next, { replace: false });
  };

  const pA = dataA?.player;
  const pB = dataB?.player;
  const bothSelected = pA && pB;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-4xl font-bold">{t('compare.title')}</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">{t('compare.subtitle')}</p>
      </div>

      {/* Slot selectors */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-center mb-8">
        <SlotHeader
          slotLabel={t('compare.playerA')}
          players={allPlayers}
          value={idA ? parseInt(idA, 10) : null}
          onSelect={(id) => setParam('a', id)}
          onClear={() => setParam('a', null)}
          data={dataA}
        />
        <div className="flex md:flex-col justify-center">
          <button
            onClick={swap}
            disabled={!idA && !idB}
            className="flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition"
            title={t('compare.swap')}
          >
            ⇄ <span className="hidden sm:inline">{t('compare.swap')}</span>
          </button>
        </div>
        <SlotHeader
          slotLabel={t('compare.playerB')}
          players={allPlayers}
          value={idB ? parseInt(idB, 10) : null}
          onSelect={(id) => setParam('b', id)}
          onClear={() => setParam('b', null)}
          data={dataB}
        />
      </div>

      {!bothSelected ? (
        <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900 rounded-lg p-10 text-center">
          <div className="text-5xl mb-3">⚖️</div>
          <div className="text-gray-500 dark:text-gray-400 text-lg font-medium">
            {(idA || idB) ? t('compare.pickOther') : t('compare.pickBoth')}
          </div>
        </div>
      ) : (
        <>
          {/* Regular / Playoffs toggle (career stats scope) */}
          <div className="flex gap-2 mb-4">
            {['regular', 'playoffs'].map(mode => (
              <button
                key={mode}
                onClick={() => setParam('type', mode === 'regular' ? null : 'playoffs')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  type === mode
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {mode === 'regular' ? t('compare.regularSeason') : t('compare.playoffs')}
              </button>
            ))}
          </div>

          {/* ── Career Stats ── */}
          <SectionCard title={t('compare.statsTitle')}>
            {(!dataA?.career?.gp && !dataB?.career?.gp) ? (
              <div className="text-center text-gray-500 dark:text-gray-400 py-4">{t('compare.noStats')}</div>
            ) : (
              <div className="max-w-2xl mx-auto">
                {/* Names row */}
                <div className="grid grid-cols-[1fr_auto_1fr] gap-2 pb-2 mb-2 border-b border-gray-100 dark:border-gray-700 text-sm font-semibold text-gray-700 dark:text-gray-200">
                  <div className="text-center truncate">{pA.lastName}</div>
                  <div className="min-w-[5.5rem]" />
                  <div className="text-center truncate">{pB.lastName}</div>
                </div>
                <CompareRow label="GP"  a={toNum(dataA?.career?.gp)}  b={toNum(dataB?.career?.gp)} />
                <CompareRow label="MPG" a={toNum(dataA?.career?.mpg)} b={toNum(dataB?.career?.mpg)} />
                <CompareRow label="PPG" a={toNum(dataA?.career?.ppg)} b={toNum(dataB?.career?.ppg)} />
                <CompareRow label="RPG" a={toNum(dataA?.career?.rpg)} b={toNum(dataB?.career?.rpg)} />
                <CompareRow label="APG" a={toNum(dataA?.career?.apg)} b={toNum(dataB?.career?.apg)} />
                <CompareRow label="SPG" a={toNum(dataA?.career?.spg)} b={toNum(dataB?.career?.spg)} />
                <CompareRow label="BPG" a={toNum(dataA?.career?.bpg)} b={toNum(dataB?.career?.bpg)} />
                <CompareRow label="TOV" a={toNum(dataA?.career?.tpg)} b={toNum(dataB?.career?.tpg)} higherIsBetter={false} />
                <CompareRow label="FG%" a={toNum(dataA?.career?.fgPct)} b={toNum(dataB?.career?.fgPct)} suffix="%" />
                <CompareRow label="3P%" a={toNum(dataA?.career?.tpPct)} b={toNum(dataB?.career?.tpPct)} suffix="%" />
                <CompareRow label="FT%" a={toNum(dataA?.career?.ftPct)} b={toNum(dataB?.career?.ftPct)} suffix="%" />
              </div>
            )}
          </SectionCard>

          {/* ── Attributes ── */}
          <SectionCard title={t('compare.attributesTitle')}>
            {(() => {
              const groups = CATEGORY_ORDER.map(k => {
                const rowsA = pA.attributes?.individual?.[k] || {};
                const rowsB = pB.attributes?.individual?.[k] || {};
                const keys = Array.from(new Set([...Object.keys(rowsA), ...Object.keys(rowsB)]));
                return { k, rowsA, rowsB, keys };
              }).filter(g => g.keys.length > 0);

              if (groups.length === 0) {
                return <div className="text-center text-gray-500 dark:text-gray-400 py-4">{t('compare.noAttributes')}</div>;
              }

              return (
                <div className="max-w-2xl mx-auto">
                  {/* Names row */}
                  <div className="grid grid-cols-[1fr_auto_1fr] gap-2 pb-2 mb-2 border-b border-gray-100 dark:border-gray-700 text-sm font-semibold text-gray-700 dark:text-gray-200">
                    <div className="text-center truncate">{pA.lastName}</div>
                    <div className="min-w-[8rem]" />
                    <div className="text-center truncate">{pB.lastName}</div>
                  </div>

                  <CompareRow label={t('compare.totalAttributes')} a={toNum(pA.attributes?.totalAttributes)} b={toNum(pB.attributes?.totalAttributes)} />

                  <div className="mt-4 space-y-5">
                    {groups.map(({ k, rowsA, rowsB, keys }) => (
                      <div key={k}>
                        <div className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                          {t(`compare.categories.${k}`)}
                        </div>
                        {keys.map(ak => {
                          const av = toNum(rowsA[ak]);
                          const bv = toNum(rowsB[ak]);
                          const win = winnerOf(av, bv, true);
                          return (
                            <div key={ak} className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 py-0.5">
                              <div className="flex justify-center">
                                <span className={`inline-flex items-center justify-center min-w-[2rem] h-7 px-1.5 rounded-md text-xs font-bold text-white ${attrBadgeClass(av)} ${win === 'a' ? 'ring-2 ring-green-500' : ''}`}>
                                  {av ?? '\u2013'}
                                </span>
                              </div>
                              <div className="text-center text-xs text-gray-600 dark:text-gray-300 min-w-[8rem]">{prettyAttr(ak)}</div>
                              <div className="flex justify-center">
                                <span className={`inline-flex items-center justify-center min-w-[2rem] h-7 px-1.5 rounded-md text-xs font-bold text-white ${attrBadgeClass(bv)} ${win === 'b' ? 'ring-2 ring-green-500' : ''}`}>
                                  {bv ?? '\u2013'}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </SectionCard>

          {/* ── Badges ── */}
          <SectionCard title={t('compare.badgesTitle')}>
            {((!pA.badgeList || pA.badgeList.length === 0) && (!pB.badgeList || pB.badgeList.length === 0)) ? (
              <div className="text-center text-gray-500 dark:text-gray-400 py-4">{t('compare.noBadges')}</div>
            ) : (
              <>
                {/* Tier count comparison */}
                <div className="max-w-2xl mx-auto mb-6">
                  <CompareRow label={t('compare.tiers.legendary')} a={pA.badgesLegendary ?? 0} b={pB.badgesLegendary ?? 0} />
                  <CompareRow label={t('compare.tiers.hof')}       a={pA.badgesHof ?? 0}       b={pB.badgesHof ?? 0} />
                  <CompareRow label={t('compare.tiers.gold')}      a={pA.badgesGold ?? 0}      b={pB.badgesGold ?? 0} />
                  <CompareRow label={t('compare.tiers.silver')}    a={pA.badgesSilver ?? 0}    b={pB.badgesSilver ?? 0} />
                  <CompareRow label={t('compare.tiers.bronze')}    a={pA.badgesBronze ?? 0}    b={pB.badgesBronze ?? 0} />
                  <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
                  <CompareRow label={t('compare.tiers.total')} a={pA.badges ?? (pA.badgeList?.length || 0)} b={pB.badges ?? (pB.badgeList?.length || 0)} />
                </div>

                {/* Each player's badge chips, grouped by category */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[pA, pB].map((p, idx) => {
                    const list = p.badgeList || [];
                    const byCat = list.reduce((acc, b) => {
                      const c = b.category || 'Other';
                      (acc[c] = acc[c] || []).push(b);
                      return acc;
                    }, {});
                    const cats = [
                      ...BADGE_CATEGORY_ORDER.filter(c => byCat[c]),
                      ...Object.keys(byCat).filter(c => !BADGE_CATEGORY_ORDER.includes(c)),
                    ];
                    return (
                      <div key={idx} className="min-w-0">
                        <div className="text-sm font-bold text-gray-800 dark:text-gray-200 mb-3 truncate">
                          {p.firstName} {p.lastName}
                        </div>
                        {list.length === 0 ? (
                          <div className="text-sm text-gray-400 dark:text-gray-500 italic">{t('compare.noBadges')}</div>
                        ) : (
                          <div className="space-y-4">
                            {cats.map(cat => {
                              const badges = [...byCat[cat]].sort(
                                (a, b) => (BADGE_TIER_RANK[a.tier] ?? 9) - (BADGE_TIER_RANK[b.tier] ?? 9)
                              );
                              return (
                                <div key={cat}>
                                  <div className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                                    {cat} ({badges.length})
                                  </div>
                                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">
                                    {badges.map((b, i) => (
                                      <BadgeCard key={`${b.name}-${i}`} name={b.name} tier={b.tier} category={cat} />
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </SectionCard>
        </>
      )}
    </div>
  );
};

export default ComparePlayers;
